import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiUrl } from '../constants/api-url';
import { BehaviorSubject, firstValueFrom, tap, catchError, of } from 'rxjs';
import { getTenant, getTenantName, setAppName, clearTenantData, getAuthToken, setAuthToken, clearAuthToken, setUserDetails, clearUserDetails } from '../shared/tenant.util';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private tokenSubject = new BehaviorSubject<string | null>(null); // Store token globally
  token$ = this.tokenSubject.asObservable(); // Observable to track token changes
  private magBypassEnabled: boolean | null = null; // Cache MAGBypass status

  /**
   * Headers that mark a call as coming from an SM front-end, for the /API gate in SM.API
   * (ApiClientGateMiddleware).
   *
   * The calls below deliberately use native fetch rather than HttpClient, so they never pass
   * through AuthInterceptor and do NOT inherit these headers from it. They target /API and are on
   * the login path, so they must set them here or the backend refuses them.
   */
  private smClientHeaders(): Record<string, string> {
    return {
      'X-SM-Client-Key': environment.smClientKey,
      'X-SM-Tenant': getTenantName()
    };
  }

  constructor(private http: HttpClient) {
    const savedToken = getAuthToken();
    if (savedToken) {
      this.tokenSubject.next(savedToken); // Load token if stored
    }
  }

  // Check if MAGBypass is enabled on backend (no token required)
  // Returns true if backend returns user data without authentication
  async checkMagBypass(): Promise<boolean> {
    // Return cached value if already checked
    if (this.magBypassEnabled !== null) {
      return this.magBypassEnabled;
    }

    try {
      // Get the tenant URL dynamically
      const baseUrl = getTenant();
      if (!baseUrl) {
        console.log('MAGBypass check: No tenant URL found');
        this.magBypassEnabled = false;
        return false;
      }

      const checkUrl = `${baseUrl}/Shared/Common/GetUserloginDetails`;
      console.log('MAGBypass check: Calling', checkUrl);

      // Use native fetch to avoid Angular HttpClient/interceptor issues
      const response = await fetch(checkUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...this.smClientHeaders()
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('MAGBypass check response:', data);

        // Check if response has userId - this indicates MAGBypass is working
        this.magBypassEnabled = data !== null && data.userId !== undefined;

        if (this.magBypassEnabled) {
          console.log('MAGBypass is ENABLED - skipping authentication prompt');

          // Persist the resolved identity, mirroring checkBackendAuth(). This
          // endpoint already returns the full user-login details; without storing
          // them, MAGBypass sessions have no 'loggedInUserDetails', so
          // getRoleContext() wrongly defaults to Buyer and the post-Accept
          // navigation on the T&C page can land on a role route that does not
          // exist in the (Buyer-default) route table -> '**' wildcard bounces the
          // user back to /terms-and-condition ("Accept does nothing").
          setUserDetails(data);
          if (data.appName) {
            setAppName(data.appName);
            console.log('[AuthService] AppName from MAGBypass:', data.appName);
          }

          // Set a placeholder token to prevent future prompts
          this.setToken('MAG_BYPASS_ENABLED');
        }
        return this.magBypassEnabled;
      } else {
        // 401 or other error - MAGBypass is disabled
        console.log('MAGBypass check: Got status', response.status, '- MAGBypass is DISABLED');
        this.magBypassEnabled = false;
        return false;
      }
    } catch (error) {
      console.log('MAGBypass check error:', error);
      this.magBypassEnabled = false;
      return false;
    }
  }

  /**
   * Check if user is authenticated on backend (via WS-Fed cookie or MAGBypass)
   * This calls the backend to see if there's an existing session
   */
  async checkBackendAuth(): Promise<boolean> {
    try {
      const baseUrl = getTenant();
      if (!baseUrl) {
        console.log('checkBackendAuth: No tenant URL found');
        return false;
      }

      const checkUrl = `${baseUrl}/Shared/Common/GetUserloginDetails`;
      console.log('checkBackendAuth: Calling', checkUrl);

      // Use fetch with credentials to send cookies
      const response = await fetch(checkUrl, {
        method: 'GET',
        credentials: 'include',  // Important: send cookies for WS-Fed session
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...this.smClientHeaders()
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('checkBackendAuth response:', data);
        
        if (data !== null && data.userId !== undefined) {
          console.log('User is authenticated on backend');
          // Store user info and set a session marker
          setUserDetails(data);
          
          // IMPORTANT: Store AppName for future API calls
          // AppName determines which customer-specific backend to use
          if (data.appName) {
            setAppName(data.appName);
            console.log('[AuthService] AppName from backend:', data.appName);
          }
          
          this.setToken('WS_FED_SESSION');
          return true;
        }
      }
      
      console.log('checkBackendAuth: Not authenticated (status:', response.status, ')');
      return false;
    } catch (error) {
      console.log('checkBackendAuth error:', error);
      return false;
    }
  }

  // Fetch Token from API (used when MAGBypass is disabled)
  fetchTokenFromAPI(username: string): Promise<{ token: string }> {
    const baseUrl = getTenant(); // Gets tenant-specific URL (e.g., https://localhost:52316/API)
    // Remove '/API' suffix if present and add '/api/Token'
    const url = baseUrl?.replace(/\/API$/i, '') + '/api/Token';
    const fullUrl = `${url}/${encodeURIComponent(username)}`;
    console.log('Fetching token from:', fullUrl);
    
    return firstValueFrom(
      this.http.get<{ token: string }>(fullUrl).pipe(
        tap(response => {
          console.log('Token received:', response);
          this.setToken(response.token);
        }),
        catchError(error => {
          console.error('Token fetch error:', error);
          throw error;
        })
      )
    );
  }

  // Set Token Globally
  setToken(token: string) {
    this.tokenSubject.next(token);
    setAuthToken(token);
  }

  // Get Current Token
  getToken(): string | null {
    return this.tokenSubject.value;
  }

  // Clear Token on Logout
  clearToken() {
    this.tokenSubject.next(null);
    clearAuthToken();
  }

  /**
   * Initiates login flow - redirects to backend auth endpoint
   * which handles WS-Federation, OIDC, or MAGBypass based on config
   */
  login(tenant?: string): void {
    const baseUrl = getTenant();
    if (!baseUrl) {
      console.error('[AuthService] Cannot login - no tenant URL configured');
      return;
    }
    
    // Remove /API suffix to get base backend URL
    const backendUrl = baseUrl.replace(/\/API$/i, '');
    const loginUrl = `${backendUrl}/auth/startlogin${tenant ? '?tenant=' + tenant : ''}`;
    
    console.log('[AuthService] Redirecting to login:', loginUrl);
    window.location.href = loginUrl;
  }

  /**
   * Initiates logout flow - clears local storage and redirects to backend logout
   * Backend will then redirect to MAG for Single Logout (SLO)
   */
  logout(): void {
    console.log('[AuthService] Initiating logout');
    
    // Get backend URL BEFORE clearing tenant data
    const baseUrl = getTenant();
    
    // Clear all local storage items
    this.clearToken();
    clearUserDetails();
    clearTenantData(); // Clear appName and tenant
    
    // Redirect to backend logout endpoint
    // Backend handles MAG SLO (Single Logout)
    if (baseUrl) {
      const backendUrl = baseUrl.replace(/\/API$/i, '');
      const logoutUrl = `${backendUrl}/auth/logout`;
      
      console.log('[AuthService] Redirecting to logout:', logoutUrl);
      window.location.href = logoutUrl;
    }
  }

  /**
   * Gets user info from backend (including AppName for tenant isolation)
   * Used to restore session after page refresh
   */
  async getUserInfo(): Promise<any> {
    const baseUrl = getTenant();
    if (!baseUrl) {
      return null;
    }
    
    const backendUrl = baseUrl.replace(/\/API$/i, '');
    const userInfoUrl = `${backendUrl}/auth/userinfo`;
    
    try {
      const response = await fetch(userInfoUrl, {
        method: 'GET',
        credentials: 'include',  // Include cookies for WS-Fed auth
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const userInfo = await response.json();
        
        // Store AppName for tenant isolation
        if (userInfo.appName) {
          setAppName(userInfo.appName);
          console.log('[AuthService] AppName from userinfo:', userInfo.appName);
        }
        
        return userInfo;
      }
      
      return null;
    } catch (error) {
      console.error('[AuthService] Error getting user info:', error);
      return null;
    }
  }
  
}

